import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { positionNameKey } from '@eombor/contracts';
import { Badge } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { Combobox } from '../../../shared/ui/Combobox.js';
import { staffApi } from '../api/staff.api.js';

/**
 * LAVOZIM — ro'yxatdan tanlanadi YOKI qo'lda yoziladi.
 *
 * Ma'lumotnomada aynan mos lavozim ko'pincha topilmaydi: kadrlar faylida
 * 63 470 yozuvdan 37 471 tasida ish joyi umuman ko'rsatilmagan va ular
 * uchun tayyor ro'yxat deyarli hech qachon javob bermaydi. Ilgari yagona
 * chora taxminan bir lavozim tanlash yoki maydonni bo'sh qoldirish edi —
 * taxmin esa hujjat tarixiga haqiqatdek chiqardi.
 *
 * Yangisini qo'shish ma'lumotnomani AXLATGA aylantirmasligi uchun takror
 * shu yerdayoq ko'rinadi: nom registr va apostrof shaklidan qat'i nazar
 * solishtiriladi (`positionNameKey` — server ham aynan shuni ishlatadi),
 * ya'ni «Yo'l ustasi» yozilganda «YO'L USTASI» ro'yxatning boshida turadi
 * va «qo'shish» qatori umuman chiqmaydi.
 */

/** Tanlangan, lekin hali yozilmagan nom — ro'yxatda o'z satri bilan turadi. */
const TYPED = '__typed__';
/** «… qo'shish» qatori. */
const CREATE = '__create__';

/** Eng qisqa lavozim nomi — shartnomadagi `positionNameSchema` bilan bir xil. */
const MIN_NAME = 2;

export interface PositionValue {
  /** Ma'lumotnomadan tanlangan lavozim. */
  id: string | null;
  /** Qo'lda yozilgan nom — `id` bo'lmaganda. */
  name: string | null;
}

export const EMPTY_POSITION: PositionValue = { id: null, name: null };

export interface PositionFieldProps {
  value: PositionValue;
  onChange: (next: PositionValue) => void;
  label: string;
  placeholder: string;
  hint?: string;
  disabled?: boolean;
}

export const PositionField = ({
  value,
  onChange,
  label,
  placeholder,
  hint,
  disabled = false,
}: PositionFieldProps) => {
  /* Yozilayotgan matn SHU YERDA: `onSearch` berilgani uchun `Combobox`
     ichki filtrlashni o'chiradi va ro'yxatni biz yig'amiz — «qo'shish»
     qatori aynan shu matndan kelib chiqadi. */
  const [query, setQuery] = useState('');

  const positions = useQuery({
    queryKey: ['staff', 'positions'],
    queryFn: () => staffApi.positions(),
  });

  const all = useMemo(() => positions.data ?? [], [positions.data]);

  const options = useMemo(() => {
    const needle = positionNameKey(query);
    const matched = all
      .filter((item) => needle === '' || positionNameKey(item.name).includes(needle))
      .map((item) => ({ value: item.id, label: item.name }));

    /* Tanlangan yangi nom ro'yxatda TURISHI kerak: `Combobox` yopilganda
       matnni `options` ichidan topib ko'rsatadi va usiz maydon bo'shab
       qolardi. */
    const head =
      value.name === null
        ? []
        : [
            {
              value: TYPED,
              label: value.name,
              badge: <Badge tone="info">{t('staff.positionNew')}</Badge>,
            },
          ];

    const exists = all.some((item) => positionNameKey(item.name) === needle);
    const sameAsTyped = value.name !== null && positionNameKey(value.name) === needle;
    const tail =
      query.trim().length >= MIN_NAME && !exists && !sameAsTyped
        ? [{ value: CREATE, label: t('staff.positionCreate', { name: query.trim() }) }]
        : [];

    return [...head, ...matched, ...tail];
  }, [all, query, value.name]);

  return (
    <Combobox
      label={label}
      placeholder={placeholder}
      hint={hint}
      emptyText={t('staff.positionEmpty')}
      loading={positions.isPending}
      disabled={disabled}
      options={options}
      value={value.id ?? (value.name === null ? null : TYPED)}
      onSearch={setQuery}
      onChange={(next) => {
        const typed = query.trim();
        setQuery('');

        if (next === CREATE) {
          onChange({ id: null, name: typed });
          return;
        }
        // `TYPED` — allaqachon tanlangani; qayta bosilsa hech narsa o'zgarmaydi.
        if (next === TYPED) return;
        onChange(next === null ? EMPTY_POSITION : { id: next, name: null });
      }}
    />
  );
};
